package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.RenderContext
import top.wcpe.allincore.ui.runtime.render.Renderer
import top.wcpe.allincore.ui.runtime.render.SlotRenderer
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * 槽位背景层渲染器：绘制背景（纹理/颜色）、占位符、边框。
 *
 * 从 [SlotRenderer] 拆分而来，对应 [RenderLayer.BACKGROUND] 层。
 */
@Suppress("MagicNumber")
class SlotBackgroundRenderer : Renderer {

    override fun layer(): RenderLayer = RenderLayer.BACKGROUND

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        render(element, box, bridge, RenderContext.NONE)
    }

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge, context: RenderContext) {
        val slot = element as Slot
        val theme = ThemeAccess.current()
        val cornerRadii = slot.cornerRadii() ?: CornerRadii.ZERO

        // 背景
        drawBackground(slot, box, bridge, cornerRadii, theme)

        // 占位符（仅在无物品时）
        if (!resolveHasItem(slot, context)) {
            val placeholderInset = slot.placeholderInset() ?: theme.slotPlaceholderInset
            drawPlaceholder(slot, box, bridge, placeholderInset)
        }

        // 边框
        val borderWidth = slot.borderWidth() ?: theme.slotBorderWidth
        if (borderWidth > 0f) {
            val borderColor = slot.borderColor() ?: theme.slotBorderColor
            drawBorder(box, bridge, borderColor, borderWidth, cornerRadii)
        }
    }

    /** 判断槽位是否有物品（静态模式或动态模式）。 */
    private fun resolveHasItem(slot: Slot, context: RenderContext): Boolean {
        val staticItem = slot.staticItem()
        if (staticItem != null) {
            return !staticItem.itemId().isNullOrEmpty()
        }
        return context.slotItem(slot.groupId(), slot.key()) != null
    }

    /**
     * 背景绘制。优先级：状态纹理 > 纹理 > 状态色 > 颜色 > 无。
     * showBackground 控制是否绘制（默认 false = 透明槽）。
     */
    private fun drawBackground(
        slot: Slot,
        box: Box,
        bridge: UiRenderBridge,
        cornerRadii: CornerRadii,
        theme: ControlTheme,
    ) {
        // 状态纹理解析与 [SlotBackdropColor] 共用，避免重复实现（issue #68）。
        val stateTexture = SlotBackdropColor.resolveStateTexture(slot)
        if (stateTexture != null) {
            bridge.drawImage(stateTexture, box.x(), box.y(), box.width(), box.height())
            return
        }
        val bgTex = slot.backgroundTexture()
        if (!bgTex.isNullOrEmpty()) {
            bridge.drawImage(bgTex, box.x(), box.y(), box.width(), box.height())
            return
        }
        if (!slot.showBackground()) {
            return
        }
        val bgColor = resolveBackgroundColor(slot, theme)
        if ((bgColor ushr 24) == 0) {
            return
        }
        if (!cornerRadii.isZero()) {
            drawRoundedBackground(box, bridge, bgColor, cornerRadii)
        } else {
            // 外层灰底 + 内缩内层填充，内缩宽度可由 backgroundBorderWidth 覆盖（默认 1px，原硬编码值）。
            val bw = slot.backgroundBorderWidth() ?: 1f
            bridge.fillRect(box.x(), box.y(), box.width(), box.height(), theme.slotBackgroundOuterColor)
            bridge.fillRect(box.x() + bw, box.y() + bw, box.width() - 2 * bw, box.height() - 2 * bw, bgColor)
        }
    }

    private fun resolveBackgroundColor(slot: Slot, theme: ControlTheme): Int =
        slot.backgroundColor() ?: theme.slotBackgroundInnerColor

    private fun drawRoundedBackground(box: Box, bridge: UiRenderBridge, color: Int, radii: CornerRadii) {
        val fill = StyledFill.builder().cornerRadii(radii.toArray()).build()
        val batch = RenderBatch(
            RenderCommandType.ROUNDED_RECT,
            null,
            RenderCommand.NO_CLIP,
            listOf(
                RenderCommand.roundedRect(
                    box.x(),
                    box.y(),
                    box.width(),
                    box.height(),
                    color,
                    RenderCommand.NO_CLIP,
                    fill,
                ),
            ),
        )
        bridge.submitBatch(batch)
    }

    private fun drawPlaceholder(slot: Slot, box: Box, bridge: UiRenderBridge, inset: Float) {
        val placeholder = slot.placeholder()
        if (placeholder.isNullOrEmpty()) {
            return
        }
        val w = box.width() - inset * 2
        val h = box.height() - inset * 2
        if (w <= 0f || h <= 0f) {
            return
        }
        bridge.drawImage(placeholder, box.x() + inset, box.y() + inset, w, h)
    }

    private fun drawBorder(box: Box, bridge: UiRenderBridge, color: Int, width: Float, cornerRadii: CornerRadii) {
        if (!cornerRadii.isZero()) {
            val fill = StyledFill.builder()
                .cornerRadii(cornerRadii.toArray())
                .borderWidth(width)
                .borderColor(color)
                .build()
            val batch = RenderBatch(
                RenderCommandType.ROUNDED_RECT,
                null,
                RenderCommand.NO_CLIP,
                listOf(
                    RenderCommand.roundedRect(
                        box.x(),
                        box.y(),
                        box.width(),
                        box.height(),
                        0x00000000,
                        RenderCommand.NO_CLIP,
                        fill,
                    ),
                ),
            )
            bridge.submitBatch(batch)
            return
        }
        val x = box.x()
        val y = box.y()
        val w = box.width()
        val h = box.height()
        bridge.fillRect(x, y, w, width, color)
        bridge.fillRect(x, y + h - width, w, width, color)
        bridge.fillRect(x, y, width, h, color)
        bridge.fillRect(x + w - width, y, width, h, color)
    }
}
